import React from "react";
import {
    Section,
    SectionTitle,
    SubSection,
    SubTitle,
    Paragraph,
    List,
    Link,
} from "./ContributingUI";

const ContributorGuidelines = () => {
    return (
        <Section id="contributor-guidelines">
            <SectionTitle>Guidelines for Accepted Contributors</SectionTitle>
            <Paragraph>
                These guidelines are for contributors working on NeST GSoC
                projects.
            </Paragraph>

            <SubSection id="goals">
                <SubTitle>Goals</SubTitle>
                <Paragraph>
                    This is a program for you to develop and merge code to NeST.
                    Your mentors will help guide you through the many steps
                    required for a successful final review and merge. While
                    writing code is the main activity, it must be supported by
                    good documentation, good examples, tests, and good
                    communication with the rest of the project.
                </Paragraph>
            </SubSection>

            <SubSection id="checklist">
                <SubTitle>Checklist</SubTitle>
                <Paragraph>
                    Here is a brief checklist of things you should familiarize
                    yourself with:
                </Paragraph>
                <List>
                    <li>
                        <strong>
                            <Link
                                href="https://gitlab.com/nitk-nest/nest/-/blob/master/CONTRIBUTING.md"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Contributing guidelines
                            </Link>
                        </strong>{" "}
                        - Documentation about contributing to NeST in general.
                    </li>
                    <li>
                        <strong>Coding style</strong> - We use{" "}
                        <Link
                            href="https://github.com/psf/black"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            black
                        </Link>{" "}
                        formatter and{" "}
                        <Link
                            href="https://www.pylint.org/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            pylint
                        </Link>{" "}
                        for code quality. Your code should pass both.
                    </li>
                    <li>
                        <strong>
                            <Link
                                href="https://nitk-nest.zulipchat.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Zulip
                            </Link>
                        </strong>{" "}
                        - Plan to read this daily during your project. This is
                        where you will communicate with mentors and the
                        community. Post weekly updates here.
                    </li>
                    <li>
                        <strong>
                            <Link
                                href="https://nest.l4.nitk.ac.in/docs/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Documentation
                            </Link>
                        </strong>{" "}
                        - Read through the docs. We follow the NumPy style for
                        Python docstrings.
                    </li>
                    <li>
                        <strong>Testing</strong> - Do you know how the test
                        framework works? Can you write your own test code?
                    </li>
                </List>
            </SubSection>

            <SubSection id="planning">
                <SubTitle>Planning</SubTitle>
                <Paragraph>Some advice:</Paragraph>
                <List>
                    <li>
                        <strong>Refine your plan.</strong> Start slow with
                        low-risk, easy-to-accomplish milestones, and work
                        towards harder ones. Think about:
                        <List>
                            <li>
                                <strong>Scope:</strong> What use cases should
                                your code support? What is specifically out of
                                scope?
                            </li>
                            <li>
                                <strong>Usability:</strong> How do you expect
                                users to use your code? What API would be most
                                natural?
                            </li>
                            <li>
                                <strong>Testing:</strong> How can you test that
                                your code works as expected?
                            </li>
                        </List>
                    </li>
                    <li>
                        <strong>Decompose into small mergeable chunks.</strong>{" "}
                        A good workflow: code something new, test it, document
                        it, post for review, iterate. It is better to accomplish
                        50% of your goals with documentation and testing
                        completed, than 100% without proper documentation and
                        tests. The latter will not be merged.
                    </li>
                    <li>
                        <strong>Commit early, commit often.</strong> This makes
                        it easier for mentors to track your progress. We expect
                        commits several times a week.
                    </li>
                </List>
            </SubSection>

            <SubSection id="communication">
                <SubTitle>Communication</SubTitle>
                <Paragraph>
                    You join our Zulip from day one - the same space the
                    maintainers use. Mentors have weekly sync calls with their
                    contributors, and we ask you to post updates on Zulip so
                    others can follow along and help.
                </Paragraph>
                <Paragraph>
                    Each project has at least two mentors - a primary and a
                    backup. Mentors are expected to review code within a day or
                    two. Published merge requests will have their own thread on
                    Zulip to keep communication specific and public.
                </Paragraph>
                <Paragraph>
                    Beyond your own project, we encourage you to help answer
                    questions, review other MRs, and join discussions about
                    other parts of the codebase. The goal is not just to finish
                    a project - we want contributors who stick around and
                    ideally come back as mentors in future years.
                </Paragraph>
            </SubSection>
        </Section>
    );
};

export default ContributorGuidelines;
